import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useModalsStore from "../../utils/stores/useModalsStore";
import {
  getCoverage,
  getObservationActivity,
  getSchoolMatrix,
  getSchoolOverview,
  getSchoolRanking,
  getStandardsAnalytics,
  getTeachersAnalytics,
} from "../../services/analyticsService";
import { getObservation, getObservations } from "../../services/observationService";
import useOrgScope from "../../utils/hooks/useOrgScope";
import { FINALIZED_STATUSES } from "../../utils/constants/observations";
import { num } from "../../utils/helpers/scoring";
import { standardScopeLabel } from "../../utils/helpers/standardScope";
import Avatar from "../../components/Avatar/Avatar";
import { formatNumericMonthDay, formatShortDate } from "../../utils/helpers/dateDisplay";
import { scoreHex, scoreTextClass, scoreTierLegend } from "../../utils/helpers/scoreColors";
import { SkeletonCard, SkeletonStatRow } from "../../components/Skeleton/Skeleton";
import RubricGate, { isRubricGated } from "../../components/RubricGate/RubricGate";
import ScheduleObservationModal from "../../components/scheduling/ScheduleObservationModal";
import { UserContext } from "../../utils/contexts/UserContext";
import { hasPermission, OBSERVATION_PERMISSION_CODES } from "../../utils/permissions";

const FINALIZED_STATUS_PARAM = [...FINALIZED_STATUSES].join(",");

// The recent table shows this many rows, so the page asks for that many.
const RECENT_LIMIT = 10;

// Past this many bars the axis names are unreadable in a 280px chart, so the
// ranking shows its two ends until asked for the whole list.
const RANKING_LEGIBLE_MAX = 15;
const RANKING_EDGE = 5;

// Each card owns its request; this is where they report in. The averages and
// the marks beneath them are ONE card, so they share one status: a failed marks
// request must not leave real percentages standing above rows that all read
// "no observations".
const PANELS_LOADING = {
  stats: "loading",
  standards: "loading",
  activity: "loading",
  recent: "loading",
};

// The ranking chart's order, matching compute_school_ranking: average
// descending, schools with no score last. Ties keep the incoming order.
const rankByScore = (rows) =>
  [...rows].sort((a, b) => {
    const av = num(a.avg_percentage);
    const bv = num(b.avg_percentage);
    if (av == null) return bv == null ? 0 : 1;
    if (bv == null) return -1;
    return bv - av;
  });

// Score-tier legend (low → high) from the single SCORE_TIERS table.
const SCORE_LEGEND_ITEMS = scoreTierLegend().map((tier) => ({
  key: tier.label,
  hex: tier.hex,
  label: tier.label,
}));

// Per-stat-card tint (icon tile + bullet). Literal class strings so Tailwind's
// content scan keeps them.
const STAT_TINTS = {
  indigo: { iconBg: "bg-indigo-50", icon: "text-indigo-600", bullet: "bg-indigo-500" },
  violet: { iconBg: "bg-violet-50", icon: "text-violet-600", bullet: "bg-violet-500" },
  emerald: { iconBg: "bg-emerald-50", icon: "text-emerald-600", bullet: "bg-emerald-500" },
  rose: { iconBg: "bg-rose-50", icon: "text-rose-600", bullet: "bg-rose-500" },
  slate: { iconBg: "bg-slate-100", icon: "text-slate-400", bullet: "bg-slate-300" },
};

export default function SchoolDashboard() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const [showSchedule, setShowSchedule] = useState(false);
  const canSchedule = hasPermission(user, OBSERVATION_PERMISSION_CODES.CREATE);
  // The coverage endpoint wants school.view AND observations…view_all, and this
  // page only proves the first. Without the second the request is a 403 and the
  // link is a dead end, so both the fetch and the link hang off this.
  const canSeeCoverage = hasPermission(
    user,
    OBSERVATION_PERMISSION_CODES.VIEW_ALL,
  );
  const openTeacherOverview = useModalsStore((s) => s.openTeacherOverview);

  const {
    canCompareSchools,
    scopeMode,
    effectiveOrgUuid,
    selectedOrgUuid,
    setSelectedOrgUuid,
  } = useOrgScope();

  const [overview, setOverview] = useState(null);
  const [standards, setStandards] = useState([]);
  // The Performance-by-Standard marks: one per teacher at a single school, one
  // per school across a network. Which they are is `scopeMode` — the same fact
  // that chose the request — so the rows don't carry their own flag for it.
  const [marks, setMarks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [recent, setRecent] = useState([]);
  const [recentBreakdown, setRecentBreakdown] = useState({});
  const [ranking, setRanking] = useState(null);
  const [showAllSchools, setShowAllSchools] = useState(false);
  const [coverage, setCoverage] = useState(null);

  const [panels, setPanels] = useState(PANELS_LOADING);

  // Each card loads on its own: sharing one Promise.all let the slowest endpoint
  // decide when anything appeared, and turned any single failure into one error
  // line in place of four good responses.
  useEffect(() => {
    if (!effectiveOrgUuid) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    setPanels(PANELS_LOADING);

    const load = (panel, request, apply) =>
      request
        .then((data) => {
          if (signal.aborted) return;
          apply(data);
          setPanels((s) => ({ ...s, [panel]: "ready" }));
        })
        .catch((err) => {
          if (signal.aborted || err?.code === "ERR_CANCELED") return;
          setPanels((s) => ({ ...s, [panel]: "error" }));
        });

    load(
      "stats",
      getSchoolOverview({ params: { organization: effectiveOrgUuid }, signal }),
      setOverview,
    );
    load(
      "activity",
      getObservationActivity({
        params: { organization: effectiveOrgUuid, days: 30 },
        signal,
      }),
      setActivity,
    );
    load(
      "recent",
      getObservations({
        params: {
          // `organization` rolls up the subtree: a head org ("all schools")
          // returns every child school's observations, a leaf just its own.
          organization: effectiveOrgUuid,
          status__in: FINALIZED_STATUS_PARAM,
          ordering: "-observed_at",
          page_size: RECENT_LIMIT,
        },
        signal,
      }),
      (data) => setRecent(data?.results || []),
    );
    return () => controller.abort();
  }, [effectiveOrgUuid]);

  // The whole Performance-by-Standard card — its averages and the marks beneath
  // them — on the one effect that knows the scope. Waiting for the subtree list
  // is the price of not guessing: before it lands every account looks like a
  // single school, which is how a 2500-teacher roster request escapes.
  useEffect(() => {
    if (!effectiveOrgUuid || scopeMode === "loading") return undefined;
    // With the subtree unknown neither branch can be trusted, so the card says
    // it failed rather than drawing every standard as "no observations".
    if (scopeMode === "unavailable") {
      setPanels((s) => ({ ...s, standards: "error" }));
      return undefined;
    }
    const controller = new AbortController();
    const { signal } = controller;
    const request =
      scopeMode === "network"
        ? // A network reads schools, never teachers: one mark per school instead
          // of thousands, and the roster endpoint would answer an un-narrowed
          // multi-school scope with an empty roster anyway. This one pass over
          // the subtree also answers the averages (compute_standards' school
          // means are this payload's network row) and the ranking chart, so
          // neither is asked for separately.
          getSchoolMatrix({ params: { organization: effectiveOrgUuid }, signal }).then(
            (data) => {
              if (signal.aborted) return;
              setStandards(
                (data?.standards || []).map((std) => ({
                  standard: std,
                  avg_percentage: data?.network?.per_standard?.[std.uuid] ?? null,
                })),
              );
              setMarks(
                (data?.schools || []).map((row) => ({
                  key: row.organization.uuid,
                  name: row.organization.name,
                  scores: row.per_standard,
                })),
              );
              setRanking(rankByScore(data?.schools || []));
            },
          )
        : Promise.all([
            getStandardsAnalytics({
              params: { organization: effectiveOrgUuid },
              signal,
            }),
            getTeachersAnalytics({
              params: { organization: effectiveOrgUuid },
              signal,
            }),
          ]).then(([standardsData, teachersData]) => {
            if (signal.aborted) return;
            // standards/teachers endpoints wrap their list in {active_rubrics, …}
            setStandards(standardsData?.standards || []);
            setMarks(
              (teachersData?.teachers || []).map((row) => ({
                key: row.teacher.uuid,
                name: row.teacher.full_name,
                teacher: row.teacher,
                scores: row.per_standard,
              })),
            );
          });

    // Same reporting as the mount requests: an empty marks row is a real "no
    // observations" only when the request that fills it actually answered.
    request
      .then(() => {
        if (signal.aborted) return;
        setPanels((s) => ({ ...s, standards: "ready" }));
      })
      .catch((err) => {
        if (signal.aborted || err?.code === "ERR_CANCELED") return;
        setPanels((s) => ({ ...s, standards: "error" }));
      });
    return () => controller.abort();
  }, [effectiveOrgUuid, scopeMode]);

  // Ranking loads on its own, deliberately: `canCompareSchools` is derived from
  // the org list, so it is false until that request lands and then flips. With
  // it in the batch's dep array the whole batch was aborted and re-fired for
  // every multi-school account — five heavy subtree scans paid twice, while the
  // server kept computing the aborted ones.
  useEffect(() => {
    if (!canCompareSchools) {
      setRanking(null);
      return undefined;
    }
    // A network's rows ride along with the matrix, which already scanned this
    // subtree for the card above — see the effect before this one.
    if (scopeMode !== "school") return undefined;
    const controller = new AbortController();
    // Only an EXPLICIT pick narrows this. Un-narrowed the endpoint spans the
    // caller's whole read scope, which is what lets an admin staff-linked at two
    // sibling schools compare them; handing it the effective org would fall back
    // to one staff link and shrink the chart to the single bar it then hides.
    getSchoolRanking({
      params: { organization: selectedOrgUuid || undefined },
      signal: controller.signal,
    })
      .then((data) => {
        if (!controller.signal.aborted) setRanking(data);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [canCompareSchools, scopeMode, selectedOrgUuid]);

  // Feeds ONE sub-line, so it reports into no panel: a 403 (school.view without
  // view_all) or a slow answer must leave the stat above it standing, and the
  // card falls back to its all-time wording. Only `totals` is read — the roster
  // itself is the coverage page's job, and above one school the server sends
  // none anyway.
  useEffect(() => {
    setCoverage(null);
    if (!effectiveOrgUuid || !canSeeCoverage) return undefined;
    const controller = new AbortController();
    getCoverage({
      params: { organization: effectiveOrgUuid },
      signal: controller.signal,
    })
      .then((data) => {
        if (!controller.signal.aborted) setCoverage(data?.totals || null);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [effectiveOrgUuid, canSeeCoverage]);

  // The list payload carries only an aggregate `percentage`, so the per-row
  // observation heatmap needs each finalized visit's per-standard scores. Fetch
  // them progressively (the table renders immediately; strips fill in as the
  // detail calls resolve) — bounded to the 10 recent rows.
  useEffect(() => {
    if (!recent.length) {
      setRecentBreakdown({});
      return undefined;
    }
    const controller = new AbortController();
    Promise.all(
      recent.map((obs) =>
        getObservation({ uuid: obs.uuid, signal: controller.signal })
          .then((detail) => [obs.uuid, detail.standard_scores || []])
          .catch(() => [obs.uuid, null]),
      ),
    ).then((entries) => {
      if (controller.signal.aborted) return;
      setRecentBreakdown(Object.fromEntries(entries));
    });
    return () => controller.abort();
  }, [recent]);

  // A standard's row of marks, worst → best: one per teacher at a single
  // school, one per school across a network (61, not 2500).
  const marksByStandard = useMemo(() => {
    const map = {};
    standards.forEach((row) => {
      const stdUuid = row.standard.uuid;
      map[stdUuid] = marks
        .map((mark) => ({ ...mark, score: num(mark.scores?.[stdUuid]) }))
        .filter((mark) => mark.score != null)
        .sort((a, b) => a.score - b.score);
    });
    return map;
  }, [standards, marks]);

  // Recharts re-lays-out every Cell, label and tick on each parent render, so
  // the 61-school rows are shaped once here instead of inside the chart's JSX.
  const rankingRows = useMemo(
    () =>
      (ranking || []).map((r) => ({
        name: r.organization.name,
        uuid: r.organization.uuid,
        avg: r.avg_percentage != null ? Number(r.avg_percentage) : 0,
        count: r.observation_count,
        fill: scoreHex(num(r.avg_percentage)),
      })),
    [ranking],
  );

  // Rows arrive sorted best → worst, so the two ends are the two slices.
  const rankingChartRows = useMemo(
    () =>
      showAllSchools || rankingRows.length <= RANKING_LEGIBLE_MAX
        ? rankingRows
        : [
            ...rankingRows.slice(0, RANKING_EDGE),
            ...rankingRows.slice(-RANKING_EDGE),
          ],
    [rankingRows, showAllSchools],
  );

  // The card's own figure counts every visit ever finalized, which answers "have
  // we ever" — the gap is a question about NOW, so the sub-line carries the
  // period instead. The gap LEADS it: this column is a quarter of the row and
  // truncates, and the number that survives has to be the one the card links
  // to. official and peer stay apart deliberately — added together they read as
  // one workload, and a school with 40 official visits is not the school with
  // 40 peer ones; peer is dropped entirely at zero rather than printing a
  // column of noughts on every school that does not run peer visits.
  const coverageSub = useMemo(() => {
    if (!coverage) return null;
    return [
      t("dashboard.coverageUncovered", { count: coverage.uncovered ?? 0 }),
      t("dashboard.coverageOfficial", { count: coverage.official ?? 0 }),
      coverage.peer > 0 ? t("dashboard.coveragePeer", { count: coverage.peer }) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [coverage, t]);

  const stats = useMemo(() => {
    const critical = overview?.critical_standard;
    return [
      {
        label: t("dashboard.totalPersonnel"),
        value: overview?.total_personnel ?? 0,
        sub: t("dashboard.totalPersonnelSub"),
        accent: "text-indigo-600",
        tint: "indigo",
        iconPath:
          "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
      },
      {
        label: t("dashboard.totalObservations"),
        value: overview?.total_observations ?? 0,
        // The sub-line still carries the coverage figures, but the card is not
        // a destination: these four are readouts, and one of them behaving like
        // a link made the row inconsistent with itself. /coverage is reachable
        // from the rail.
        sub: coverageSub || t("dashboard.totalObservationsSub"),
        accent: "text-violet-600",
        tint: "violet",
        iconPath:
          "M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10",
      },
      {
        label: t("dashboard.avgPerformance"),
        value:
          overview?.avg_percentage != null
            ? `${Math.round(num(overview.avg_percentage))}%`
            : "—",
        sub: t("dashboard.avgPerformanceSub"),
        accent: scoreTextClass(num(overview?.avg_percentage)),
        tint: "emerald",
        iconPath:
          "M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941",
      },
      {
        label: t("dashboard.criticalDomain"),
        value: critical ? t("common.standardShort", { number: critical.number }) : "—",
        // The scope leads: a subject-scoped mean is not a whole-school figure,
        // and the title is what truncates.
        sub: critical
          ? [standardScopeLabel(t, critical), critical.title].filter(Boolean).join(" · ")
          : t("dashboard.noDataYet"),
        accent: critical ? "text-rose-600" : "text-slate-400",
        tint: critical ? "rose" : "slate",
        iconPath:
          "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z",
      },
    ];
  }, [overview, coverageSub, t]);

  // `focusStandardUuid` is passed only from the Performance-by-Standard bars
  // (a bar belongs to one standard); the department marks and
  // recent-observation rows open the full all-standards overview.
  const handleTeacherClick = (teacherWrapper, focusStandardUuid) => {
    if (!teacherWrapper) return;
    const tt = teacherWrapper.teacher || teacherWrapper;
    openTeacherOverview({ teacher: tt, focusStandardUuid });
  };

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
              {overview?.organization?.name || t("dashboard.title")}
            </h2>
            <p className="text-sm text-slate-500 mt-1">{t("dashboard.subtitle")}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {canSchedule && (
              <button
                onClick={() => setShowSchedule(true)}
                className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 shadow-lg shadow-indigo-100 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {t("schedule.scheduleObservation")}
              </button>
            )}
          </div>
        </div>

        <Panel status={panels.stats} skeleton={<SkeletonStatRow count={4} />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => {
            const tint = STAT_TINTS[stat.tint] || STAT_TINTS.slate;
            // Every card is a readout. None is clickable, and none pretends to
            // be: no hover lift, no ring, nothing that invites a click it will
            // not answer.
            return (
              <div
                key={stat.label}
                className="block bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    {stat.label}
                  </p>
                  <span
                    className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${tint.iconBg}`}
                  >
                    <svg
                      className={`w-[18px] h-[18px] ${tint.icon}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="1.8"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={stat.iconPath} />
                    </svg>
                  </span>
                </div>
                <p className={`text-4xl font-black tracking-tight mt-4 ${stat.accent}`}>
                  {stat.value}
                </p>
                {stat.sub && (
                  <p className="flex items-center gap-1.5 mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${tint.bullet}`} />
                    {/* The coverage line runs to three figures and this column
                        is a quarter of the row — `title` is what keeps the
                        truncated tail readable at all. */}
                    <span className="truncate" title={stat.sub}>
                      {stat.sub}
                    </span>
                  </p>
                )}
              </div>
            );
          })}
        </div>
        </Panel>

        {/* One bar ranks nothing: drilled into a single school the chart has
            nobody left to compare it with. */}
        {canCompareSchools && rankingRows.length > 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {t("dashboard.schoolRanking")}
                </h3>
                <p className="text-xs text-slate-500">
                  {rankingChartRows.length < rankingRows.length
                    ? t("dashboard.schoolRankingEnds", {
                        shown: rankingChartRows.length,
                        count: rankingRows.length,
                      })
                    : t("dashboard.schoolRankingSub")}
                </p>
              </div>
              {rankingRows.length > RANKING_LEGIBLE_MAX && (
                <button
                  type="button"
                  onClick={() => setShowAllSchools((v) => !v)}
                  className="shrink-0 text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded-md px-1"
                >
                  {showAllSchools
                    ? t("dashboard.showFewerSchools")
                    : t("dashboard.showAllSchools", { count: rankingRows.length })}
                </button>
              )}
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rankingChartRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  {/* interval={0} forces a label under EVERY bar — recharts
                      otherwise silently drops ticks that don't fit, and the
                      long Kazakh school names made the widest label vanish.
                      Truncated here; the tooltip carries the full name. */}
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    tickFormatter={(name) =>
                      name.length > 22 ? `${name.slice(0, 21)}…` : name
                    }
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name, entry) =>
                      name === "avg"
                        ? [value ? `${value.toFixed(1)}%` : "—", t("dashboard.avg")]
                        : [value, name]
                    }
                  />
                  <Bar
                    dataKey="avg"
                    radius={[8, 8, 0, 0]}
                    onClick={(data) => {
                      if (data?.uuid) setSelectedOrgUuid(data.uuid);
                    }}
                  >
                    {rankingChartRows.map((row) => (
                      <Cell key={row.uuid} fill={row.fill} />
                    ))}
                    <LabelList
                      dataKey="avg"
                      position="top"
                      formatter={(v) => (v ? `${Math.round(v)}%` : "—")}
                      style={{ fontSize: 11, fontWeight: 700 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <Panel status={panels.standards} skeleton={<SkeletonCard className="h-72" lines={6} />}>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {t("dashboard.performanceByStandard")}
              </h3>
              <p className="text-xs text-slate-500">
                {/* The marks change meaning with the scope, so the sub line says
                    which they are rather than leaving 61 unlabelled squares. */}
                {scopeMode === "network"
                  ? t("dashboard.schoolMarksHint")
                  : t("dashboard.performanceByStandardSub")}
              </p>
            </div>
            <Link
              to="/standards"
              className="shrink-0 flex items-center gap-1 text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded-md px-1"
            >
              {t("dashboard.viewDetailedReport")}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          {isRubricGated(overview?.active_rubrics) ? (
            // Network spans >1 active rubric: per-standard rollup can't be
            // combined, so prompt a single-school drill-down via the header
            // selector instead of fabricating numbers.
            <RubricGate
              activeRubrics={overview.active_rubrics}
              className="border-0 shadow-none py-6"
            />
          ) : standards.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              {t("dashboard.noRubricData")}
            </p>
          ) : (
            <div className="space-y-3">
              {standards.map((row) => {
                const score = num(row.avg_percentage);
                const marks = marksByStandard[row.standard.uuid] || [];
                const rowTitle = `${t("teacher.standardN", { number: row.standard.number })}: ${row.standard.title}`;
                const scopeLabel = standardScopeLabel(t, row.standard);
                return (
                  <div
                    key={row.standard.uuid}
                    className="relative p-3 rounded-xl hover:bg-slate-50 focus-within:bg-slate-50 transition-colors"
                  >
                    {/* Stretched link: the whole row navigates; the marks are
                        sibling buttons layered above it (z-10), so no
                        interactive nesting. */}
                    <Link
                      to={`/standards/${row.standard.uuid}`}
                      state={{ from: "/" }}
                      aria-label={[rowTitle, scopeLabel].filter(Boolean).join(" — ")}
                      className="absolute inset-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                    />
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 min-w-0 mb-2">
                          <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest truncate">
                            {rowTitle}
                          </p>
                          {scopeLabel && (
                            <span className="shrink-0 max-w-[50%] truncate text-[10px] font-semibold text-indigo-600">
                              {scopeLabel}
                            </span>
                          )}
                        </div>
                        <div className="flex items-end gap-0.5 flex-wrap">
                          {marks.length === 0 ? (
                            <span className="text-[10px] text-slate-500">
                              {t("dashboard.noObservations")}
                            </span>
                          ) : (
                            marks.map((mark) => {
                              const label = `${mark.name} — ${Math.round(mark.score)}%`;
                              return (
                                <button
                                  type="button"
                                  key={mark.key}
                                  title={label}
                                  aria-label={label}
                                  onClick={() =>
                                    scopeMode === "network"
                                      ? setSelectedOrgUuid(mark.key)
                                      : handleTeacherClick(
                                          { teacher: mark.teacher },
                                          row.standard.uuid,
                                        )
                                  }
                                  className="relative z-10 w-3 h-6 rounded-[3px] cursor-pointer hover:opacity-60 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                                  style={{ backgroundColor: scoreHex(mark.score) }}
                                />
                              );
                            })
                          )}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 w-16 text-lg font-black ${scoreTextClass(score)} text-right`}
                      >
                        {score != null ? `${Math.round(score)}%` : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {standards.length > 0 && <Legend items={SCORE_LEGEND_ITEMS} />}
        </div>
        </Panel>

        <Panel status={panels.activity} skeleton={<SkeletonCard className="h-64" lines={5} />}>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {t("dashboard.activity")}
              </h3>
              <p className="text-xs text-slate-500">
                {t("dashboard.activitySub")}
              </p>
            </div>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={activity.map((d) => ({ date: d.date, count: d.count }))}>
                <defs>
                  <linearGradient id="activityFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={formatNumericMonthDay}
                  interval={Math.max(0, Math.floor(activity.length / 10))}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={(d) => formatShortDate(d)}
                  formatter={(value) => [value, t("dashboard.observations")]}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#6366f1"
                  strokeWidth={2}
                  fill="url(#activityFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        </Panel>

        <Panel status={panels.recent} skeleton={<SkeletonCard className="h-80" lines={6} />}>
          <RecentObservationsTable
            observations={recent}
            breakdown={recentBreakdown}
            standardCount={standards.length}
            onSelect={(obs) =>
              openTeacherOverview({ teacher: obs.observed_teacher })
            }
          />
        </Panel>
      </div>

      {showSchedule && (
        <ScheduleObservationModal
          onClose={() => setShowSchedule(false)}
          onSubmitted={() => setShowSchedule(false)}
        />
      )}
    </div>
  );
}

// One card's share of the page. Each request reports into its own panel, so a
// slow or failed endpoint costs that card alone — the mount requests used to
// share a Promise.all, where one failure erased every good response with it.
function Panel({ status, skeleton, children }) {
  const { t } = useTranslation();
  if (status === "loading") return skeleton;
  if (status === "error") {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm text-center text-sm text-slate-400">
        {t("dashboard.panelError")}
      </div>
    );
  }
  return children;
}

function RecentObservationsTable({ observations, breakdown, standardCount, onSelect }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="text-lg font-bold text-slate-900">
          {t("dashboard.recentObservations")}
        </h3>
        <p className="text-xs text-slate-500">
          {t("dashboard.recentObservationsSub", { count: observations.length })}
        </p>
      </div>
      {observations.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-slate-400">
          {t("dashboard.noObservationsYet")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-50">
                <th className="px-6 py-3">{t("dashboard.colTeacher")}</th>
                <th className="px-6 py-3">{t("dashboard.colConductedBy")}</th>
                <th className="px-6 py-3">{t("dashboard.colSubject")}</th>
                <th className="px-6 py-3">{t("dashboard.colDepartment")}</th>
                <th className="px-6 py-3">{t("dashboard.colDate")}</th>
                <th className="px-6 py-3">{t("dashboard.colGrade")}</th>
                <th className="px-6 py-3 text-right">{t("dashboard.colScore")}</th>
                <th className="px-6 py-3">{t("dashboard.observationHeatmap")}</th>
              </tr>
            </thead>
            <tbody>
              {observations.map((obs) => {
                const teacher = obs.observed_teacher || {};
                const pct = obs.percentage != null ? num(obs.percentage) : null;
                return (
                  <tr
                    key={obs.uuid}
                    onClick={() => onSelect(obs)}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar
                          user={teacher}
                          className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 text-[11px] font-black shrink-0"
                        />
                        {/* Keyboard path into the row's onClick action — the
                            tr itself is not focusable. */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(obs);
                          }}
                          className="font-bold text-slate-800 hover:text-indigo-600 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                        >
                          {teacher.full_name || "—"}
                        </button>
                      </div>
                    </td>
                    {/* The MAIN observer, not `created_by`: the moment a manager
                        books a visit for an evaluator those two are different
                        people, and only one of them walked into the lesson. */}
                    <td className="px-6 py-3.5">
                      {obs.main_observer?.full_name ? (
                        <span className="text-[13px] font-semibold text-slate-600">
                          {obs.main_observer.full_name}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5">
                      {obs.domain_subject?.name ? (
                        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {obs.domain_subject.name}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5">
                      {teacher.department ? (
                        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {teacher.department}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-slate-600">
                      {formatShortDate(obs.observed_at)}
                    </td>
                    <td className="px-6 py-3.5">
                      {obs.group?.name ? (
                        <span className="inline-flex px-2.5 py-1 rounded-lg bg-slate-100 text-xs font-bold text-slate-600">
                          {obs.group.name}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td
                      className={`px-6 py-3.5 text-base font-black text-right ${scoreTextClass(pct)}`}
                    >
                      {pct != null ? `${Math.round(pct)}%` : "—"}
                    </td>
                    <td className="px-6 py-3.5">
                      <ObservationHeatmap
                        scores={breakdown?.[obs.uuid]}
                        count={standardCount}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// A per-observation strip: one mini-bar per evaluation standard, colored by that
// lesson's per-standard score. `scores` is the detail endpoint's
// `standard_scores` array, `undefined` while the detail is still loading, or
// `null` if it failed to load.
function ObservationHeatmap({ scores, count = 8 }) {
  if (scores === undefined) {
    return (
      <div className="flex items-center gap-1" aria-hidden="true">
        {Array.from({ length: count || 8 }).map((_, i) => (
          <span
            key={i}
            className="w-3.5 h-3.5 rounded-[4px] bg-slate-100 animate-pulse"
          />
        ))}
      </div>
    );
  }
  if (!scores || scores.length === 0) {
    return <span className="text-[10px] text-slate-300">—</span>;
  }
  // One slot per standard, by its place in the rubric: a lesson not scored on
  // a subject-specific standard leaves a gap there instead of shifting every
  // square after it out of line with the rows above and below.
  const byOrder = new Map(scores.map((s) => [s.standard_order, s]));
  const slots = Math.max(count || 0, ...scores.map((s) => s.standard_order ?? 0));
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: slots }, (_, i) => {
        const s = byOrder.get(i + 1);
        if (!s) {
          return (
            <span
              key={`gap-${i}`}
              aria-hidden="true"
              className="w-3.5 h-3.5 rounded-[4px] border border-dashed border-slate-200"
            />
          );
        }
        const pct = num(s.percentage);
        const notObserved = s.summary === "not_observed";
        const color = notObserved ? "#e2e8f0" : scoreHex(pct);
        return (
          <span
            key={s.uuid}
            title={`${s.standard_code ? `${s.standard_code} · ` : ""}${
              notObserved || pct == null ? "—" : `${Math.round(pct)}%`
            }`}
            className="w-3.5 h-3.5 rounded-[4px]"
            style={{ backgroundColor: color }}
          />
        );
      })}
    </div>
  );
}

// A "Teachers by X" distribution panel: one row per group (subject or
// department), each row a wrap of score-colored teacher squares. `rows` =
// [{ name, teachers, count }]; `labelFor(name)` resolves the display label
// (e.g. the empty-bucket sentinel → a localized "— No …"). Shared by the
// by-subject and by-department panels so they never drift.
// Swatch row used under the Performance-by-Standard panel.
function Legend({ items }) {
  return (
    <div className="flex items-center justify-center gap-x-4 gap-y-1.5 flex-wrap mt-5 pt-4 border-t border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
      {items.map((item) => (
        <span key={item.key} className="flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-[3px]"
            style={{ backgroundColor: item.hex }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}
